import { useEffect, useMemo, useState } from "react";
import type {
  StockDetailData,
  StockHolderNumberPoint,
  StockHolderTradeRow,
  StockKlineBar,
} from "fin-web-components";
import { StockApi } from "./StockApi";

export interface StockDetailDataState {
  detail: StockDetailData | null;
  dayKlines: StockKlineBar[] | null;
  holderNumbers: StockHolderNumberPoint[] | null;
  holderTrades: StockHolderTradeRow[] | null;
  loading: boolean;
  klineLoading: boolean;
  holderTradesLoading: boolean;
  navTitle: string;
  navSubtitle: string | undefined;
}

export function useStockDetailData(stockCode: string): StockDetailDataState {
  const [detail, setDetail] = useState<StockDetailData | null>(null);
  const [dayKlines, setDayKlines] = useState<StockKlineBar[] | null>(null);
  const [holderNumbers, setHolderNumbers] = useState<StockHolderNumberPoint[] | null>(null);
  const [holderTrades, setHolderTrades] = useState<StockHolderTradeRow[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [klineLoading, setKlineLoading] = useState(true);
  const [holderTradesLoading, setHolderTradesLoading] = useState(true);

  useEffect(() => {
    const code = stockCode.trim();
    if (!code) {
      return;
    }
    let cancelled = false;
    setLoading(true);
    setKlineLoading(true);
    setHolderTradesLoading(true);
    setDetail(null);
    setDayKlines(null);
    setHolderNumbers(null);
    setHolderTrades(null);

    void (async () => {
      const [data, klines, numbers, trades] = await Promise.all([
        StockApi.queryStockDetailInfo(code),
        StockApi.queryStockDayKlines(code),
        StockApi.queryStockHolderNumbers(code),
        StockApi.queryStockHolderTrades(code),
      ]);
      if (cancelled) {
        return;
      }
      setDetail(data);
      setDayKlines(klines);
      setHolderNumbers(numbers);
      setHolderTrades(trades);
      setLoading(false);
      setKlineLoading(false);
      setHolderTradesLoading(false);
    })();

    return () => {
      cancelled = true;
    };
  }, [stockCode]);

  const navTitle = useMemo(() => {
    const name = detail?.stockName?.trim();
    if (name) {
      return name;
    }
    return stockCode.trim() || "股票详情";
  }, [detail?.stockName, stockCode]);

  const navSubtitle = useMemo(() => {
    const code = detail?.stockCode?.trim() || stockCode.trim();
    return code || undefined;
  }, [detail?.stockCode, stockCode]);

  return {
    detail,
    dayKlines,
    holderNumbers,
    holderTrades,
    loading,
    klineLoading,
    holderTradesLoading,
    navTitle,
    navSubtitle,
  };
}
